import { type FC } from "react";
import { Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import ModalHeader from "@/components/atoms/ModalHeader";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import { useGenerateSlots } from "@/services/ReceptionSettings";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

interface Props {
  opened: boolean;
  companyId: number;
  onClose: () => void;
}

/**
 * Confirms and triggers POST .../slots/generate for today through Dec 31 of the current year
 * (handoff §11.3). Deliberately not a free date-range picker — generating across a year boundary
 * doesn't work reliably on the backend, so the range is fixed to what's known to work.
 */
const GenerateSlotsModal: FC<Props> = ({ opened, companyId, onClose }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const fromDate = dayjs().startOf("day");
  const toDate = dayjs().endOf("year").startOf("day");
  const dayjsLocale = i18n.language.startsWith("no") ? "nb" : "en";

  const { mutate: generateSlots, isPending } = useGenerateSlots(companyId, {
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.saveSuccess"),
          t("settings:reception.timeSlots.generateSuccess")
        )
      );
      queryClient.invalidateQueries({ queryKey: ["receptionCalendarSlots"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["receptionSlots"], exact: false });
      onClose();
    },
    onError: () =>
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.updateError"), t("notifications:generic.updateError"))
      ),
  });

  const handleGenerate = () => {
    generateSlots({
      fromDate: fromDate.format("YYYY-MM-DD"),
      toDate: toDate.format("YYYY-MM-DD"),
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      title={<ModalHeader title={t("settings:reception.timeSlots.generateSlots")} svgAsset="schedule" />}
    >
      <Stack gap="md" p="lg">
        <Text size="sm" color="secondary">
          {t("settings:reception.timeSlots.generateDescription")}
        </Text>
        <Text size="md" strong data-testid="generate-slots-range">
          {fromDate.locale(dayjsLocale).format("D. MMM YYYY")} – {toDate.locale(dayjsLocale).format("D. MMM YYYY")}
        </Text>
      </Stack>
      <ModalCTAs>
        <CTA intent="secondary" onClick={onClose}>
          {t("actions:cancel")}
        </CTA>
        <CTA intent="primary" onClick={handleGenerate} loading={isPending} data-testid="confirm-generate-slots">
          {t("settings:reception.timeSlots.generateSlots")}
        </CTA>
      </ModalCTAs>
    </Modal>
  );
};

export default GenerateSlotsModal;
